'use client';

import { useMemo } from 'react';
import type { ScoredTechnology } from '@/lib/tech-stack-recommender';

interface ArchitectureDiagramProps {
  technologies: ScoredTechnology[];
  maxTechnologies?: number;
}

interface ArchitectureLayer {
  name: string;
  description: string;
  categories: string[];
  color: string;
  technologies: ScoredTechnology[];
}

export default function TechStackArchitectureDiagram({
  technologies,
  maxTechnologies = 10,
}: ArchitectureDiagramProps) {
  const architectureLayers = useMemo<ArchitectureLayer[]>(() => {
    // Define architecture layers from top to bottom
    const layers: Omit<ArchitectureLayer, 'technologies'>[] = [
      {
        name: 'Frontend',
        description: 'User-facing applications and interfaces',
        categories: ['frontend-framework', 'mobile-framework'],
        color: '#E89841', // Orange
      },
      {
        name: 'API Layer',
        description: 'API gateways and backend services',
        categories: ['api-gateway', 'backend-framework'],
        color: '#4A90E2', // Blue
      },
      {
        name: 'Business Logic',
        description: 'Application servers and runtimes',
        categories: ['runtime', 'backend-framework'],
        color: '#50C878', // Green
      },
      {
        name: 'Data Layer',
        description: 'Databases and caching systems',
        categories: ['database', 'cache', 'search'],
        color: '#9B59B6', // Purple
      },
      {
        name: 'Message Queue',
        description: 'Asynchronous communication and event streaming',
        categories: ['message-queue'],
        color: '#E74C3C', // Red
      },
      {
        name: 'Infrastructure',
        description: 'Cloud providers, orchestration, and CDN',
        categories: ['cloud-provider', 'container-orchestration', 'cdn'],
        color: '#F39C12', // Yellow/Orange
      },
      {
        name: 'Observability',
        description: 'Monitoring, logging, and analytics',
        categories: ['monitoring', 'logging', 'analytics', 'observability'],
        color: '#1ABC9C', // Teal
      },
      {
        name: 'Security & Auth',
        description: 'Authentication, secrets, and access control',
        categories: ['authentication', 'secret-management'],
        color: '#E67E22', // Dark Orange
      },
      {
        name: 'CI/CD & Testing',
        description: 'Continuous integration, deployment, and testing',
        categories: ['ci-cd', 'testing'],
        color: '#3498DB', // Light Blue
      },
      {
        name: 'Communication',
        description: 'Email and notification services',
        categories: ['email-service'],
        color: '#95A5A6', // Gray
      },
    ];

    // Map technologies to layers
    return layers.map((layer) => ({
      ...layer,
      technologies: technologies
        .filter((tech) => layer.categories.includes(tech.category))
        .slice(0, 3), // Max 3 technologies per layer for cleaner diagram
    }));
  }, [technologies]);

  const hasAnyTechnologies = architectureLayers.some((layer) => layer.technologies.length > 0);

  if (!hasAnyTechnologies) {
    return (
      <div className="bg-surface rounded-lg p-8 text-center">
        <p className="text-foreground-secondary">
          Complete the questionnaire to see your recommended architecture
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-foreground mb-2">Target Architecture</h3>
        <p className="text-foreground-secondary">
          Recommended technology stack organized by architectural layers
        </p>
      </div>

      <div className="space-y-4">
        {architectureLayers
          .filter((layer) => layer.technologies.length > 0)
          .map((layer, layerIndex) => (
            <div
              key={layer.name}
              className="relative"
            >
              {/* Layer Card */}
              <div
                className="bg-surface border-2 rounded-lg p-6 transition-all hover:shadow-lg"
                style={{ borderColor: `${layer.color}40` }}
              >
                {/* Layer Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: layer.color }}
                      />
                      <h4 className="text-lg font-bold text-foreground">{layer.name}</h4>
                    </div>
                    <p className="text-sm text-foreground-tertiary">{layer.description}</p>
                  </div>
                </div>

                {/* Technologies in this layer */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {layer.technologies.map((tech) => (
                    <div
                      key={tech.id}
                      className="bg-background rounded-lg p-4 border border-border-muted hover:border-border-muted0 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="font-semibold text-foreground text-sm mb-1">
                            {tech.name}
                          </div>
                          <div className="text-xs text-foreground-tertiary capitalize">
                            {tech.category.replace(/-/g, ' ')}
                          </div>
                        </div>
                        <div
                          className="text-xs font-bold px-2 py-1 rounded"
                          style={{
                            backgroundColor: `${layer.color}20`,
                            color: layer.color,
                          }}
                        >
                          {tech.score.toFixed(0)}
                        </div>
                      </div>
                      <p className="text-xs text-foreground-tertiary line-clamp-2">
                        {tech.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Connector Arrow to next layer */}
              {layerIndex < architectureLayers.filter((l) => l.technologies.length > 0).length - 1 && (
                <div className="flex justify-center my-2">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="text-foreground-muted"
                  >
                    <path
                      d="M12 5v14m0 0l-4-4m4 4l4-4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}
            </div>
          ))}
      </div>

      {/* Legend */}
      <div className="bg-surface rounded-lg p-6 mt-8">
        <h4 className="text-sm font-bold text-foreground mb-4">Architecture Layers</h4>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {architectureLayers
            .filter((layer) => layer.technologies.length > 0)
            .map((layer) => (
              <div key={layer.name} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: layer.color }}
                />
                <span className="text-xs text-foreground-secondary">{layer.name}</span>
              </div>
            ))}
        </div>
      </div>

      {/* Architecture Notes */}
      <div className="bg-accent-muted border border-border rounded-lg p-6">
        <h4 className="font-bold text-accent mb-2">Architecture Notes</h4>
        <ul className="space-y-2 text-sm text-foreground-secondary">
          <li className="flex items-start gap-2">
            <span className="text-accent">•</span>
            <span>
              This diagram shows the recommended technologies organized into architectural layers from frontend to infrastructure.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-accent">•</span>
            <span>
              Each layer shows up to 3 top-scoring technologies based on your requirements.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-accent">•</span>
            <span>
              Technologies are ordered by relevance score (shown in the colored badge).
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-accent">•</span>
            <span>
              Use the detailed comparison and charts below to evaluate specific options within each layer.
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
}
